import { Component, computed, EventEmitter, inject, Input, input, Output, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductCardTooltipService } from "@components/product-card/product-card-tooltip.service";
import { ProductImageComponent } from "@components/product-image/product-image.component";
import { DisplayNamePipe, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { ProductPriceData } from "@domain/product/product-price-data";
import { ProductService } from "@domain/product/product.service";
import { UserService } from "@domain/user/user.service";
import { CheckBoxControl } from "@odx/angular/cdk/checkbox-control";
import { map } from "rxjs";

@Component({
  selector: "app-product-card",
  templateUrl: "./product-card.component.html",
  styleUrls: ["./product-card.component.scss"],
  imports: [UiKitModule, ProductImageComponent, LocalizeProductNamePipe, DisplayNamePipe],
  standalone: true,
})
export class ProductCardComponent {
  private tooltipService = inject(ProductCardTooltipService);
  private productService = inject(ProductService);
  private userService = inject(UserService);

  readonly product = input.required<Product>();
  readonly showCosts = input<boolean>(false);
  readonly selectionMode = input<"none" | "single" | "multi">("none");
  readonly imageUrl = input<string>();
  readonly fallbackImageUrl = input<string>();

  @Output() select$ = new EventEmitter<boolean>();

  @ViewChild("checkbox")
  protected checkbox?: CheckBoxControl;
  protected tooltipVisible: boolean = false;
  protected selected = false;

  @Input() set isSelected(selected: boolean) {
    if (selected) {
      this.select$.emit(selected);
    }
    this.selected = selected;
  }

  private readonly showZrefPrices = toSignal(this.userService.user$.pipe(map((user) => user.showZrefPrices)), {
    initialValue: false,
  });

  protected readonly priceData = computed<ProductPriceData | undefined>(() => {
    const showZref = this.showZrefPrices();
    const regularPrice = this.productService.getPriceData(this.product());
    const zrefPrice = this.productService.getZrefPriceData(this.product());
    if (showZref) {
      return zrefPrice;
    }
    return regularPrice;
  });

  constructor() {
    this.tooltipService.visibleTooltip.subscribe(() => {
      this.tooltipVisible = false;
    });
  }

  protected onTooltipClicked($event: MouseEvent) {
    if (this.isTooltipDisabled()) {
      return;
    }
    if (!this.tooltipVisible) {
      this.tooltipService.tooltipSelected($event.currentTarget);
    }
    this.tooltipVisible = !this.tooltipVisible;
  }

  protected isTooltipDisabled(): boolean {
    return this.product().name.length <= 80;
  }

  protected onCardClicked() {
    if (this.checkbox) {
      this.checkbox.checked = !this.checkbox.checked;
      this.select$.emit(this.checkbox.checked);
    }
  }

  protected onCheckedChanged() {
    if (this.checkbox) {
      this.select$.emit(this.checkbox.checked);
    }
  }
}
